import { Navigate, useLocation, useNavigate } from "react-router";
import { Route, Routes } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Fab, Tooltip, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";

import Patients from "pages/patients/Patients";
import PatientRPM from "../../containers/PatientRPM";
import Prohibited from "../../containers/Prohibited";
import Tasks from "pages/tasks/tasks";
import Settings from "pages/settings/Settings";
import Billing from "../Billing";

import Header from "./Header";
import Sidenav from "./Sidenav";
import Dashboard from "pages/dashboard/Dashboard";
import WearableView from "pages/cognito/preview/WeareableView";
import PatchMonitoring from "pages/patchMonitoring/PatchMonitoring";
import {
  PATCH_ROUTE,
  PATCH_TABS,
  patchTabRoute,
} from "pages/patchMonitoring/patchMonitoring.config";

import BillingAmount from "pages/billingAmount/BillingAmount";
import ManualUpload from "pages/manualUpload/ManualUpload";
import ReportTemplates from "pages/reportTemplates/ReportTemplates";
import { EHRJobs } from "../../components/Hl7Jobs";
import { ReportsAssigment } from "../../components/ReportsAssigment";

import { CLINIC_ROLES } from "../../static/constants";

import Transmission from "pages/transmission/Transmission";
import TransmissionReport from "components/pages/transmissions/modal/detail/TransmissionReport";
import { useDispatch } from "react-redux";
import "./index.css";

import { PageNotFound } from "containers/PageNotFound";
import ArchivedReports from "pages/archivedReports/ArchivedReports";
import { ProtectedRoute } from "utils/routes.util";
import { USER_ROLES } from "../../static/constants";
import DeactivatePatientModal from "components/pages/transmissions/modal/patient/DeactivatePatientModal";
import SessionExpiryModal from "components/common/modals/sessionExpiryModal/SessionExpiryModal";
import useSessionExpiry from "hooks/useSessionExpiry";
import { useSelector } from "react-redux";
import RpmView from "pages/rpm/RPMView";
import {
  KeyboardDoubleArrowLeftRounded,
  KeyboardDoubleArrowRightRounded,
} from "@mui/icons-material";
import NonCompliantPatients from "pages/nonCompliantPatients/NonCompliantPatients";
import { useGetAccountsQuery } from "app/store/transmission/api/transmissionTableApi";
import {
  loadAccounts,
  loadAccountsMapper,
  loadAllAccounts,
} from "app/store/account/slice/accountSlice";
import Loading from "components/common/loading/Loading";
import useCheckRPMSaaSAccountUser from "hooks/useCheckRPMSaaSAccountUser";
import { getRolePreferenceByAccount } from "utils/accountPreference";
import { handleLogout, setNavigateFunction } from "utils/auth";
import { fetchProfile } from "app/services/user/user.service";
import { setCurrentUser } from "app/store/auth/slice/authSlice";
import { showErrorToast } from "utils/toast";
import { checkQuickTextTemplateAccess } from "utils/account.util";
import AccountConfiguration from "pages/accountConfiguration/AccountConfiguration";
import PatientProfile from "components/pages/transmissions/modal/patient/PatientProfile";

const AppPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { isExtending, extendSession, showExpiryWarning } = useSessionExpiry();

  const handleSessionLogout = async () => {
    setIsLoggingOut(true);
    try {
      await handleLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();

        dispatch(setCurrentUser(response?.data));
      } catch (err) {
        showErrorToast(err?.message || "Failed to fetch user profile");
        handleLogout(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch]);

  const { currentUser } = useSelector((state) => state.auth);
  const { accounts } = useSelector((state) => state.account);
  const { isRPMViewLoading } = useSelector((state) => state.rpm);
  const { isReportDetailModal } = useSelector((state) => state.transmission);

  const hasSettingsAccess = checkQuickTextTemplateAccess(accounts, currentUser);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const isRPMView = window.location.pathname === "/rpm";
  const isOldRpmView = window.location.pathname === "/patient/rpm";

  const classes = makeStyles((theme) => ({
    root: {
      display: "flex",
      position: "relative",
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
      paddingTop: 70,
    },
  }));

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  const [view, setView] = React.useState(null);
  const [role, setRole] = React.useState(null);
  const [physician, setPhysician] = React.useState(false);
  const [clinicName, setClinicName] = React.useState("");

  useEffect(() => {
    if (["/rpm", "/wearables", "/prohibited"].includes(location?.pathname)) {
      localStorage.removeItem("previousPath");
      return;
    }

    localStorage.setItem("previousPath", location?.pathname);
  }, [location?.pathname]);

  useEffect(() => {
    if (!role) {
      const savedRole = localStorage.getItem("role");
      const savedClinicName = localStorage.getItem("clinicName");
      const savedPhysician = localStorage.getItem("physician") === "true";
      setRole(savedRole);
      setClinicName(savedClinicName);
      setPhysician(savedPhysician);
    }
  }, [role]);

  const theme = useTheme();

  const setActiveView = (newView) => {
    setView(newView);
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const { data: allAccounts, isLoading: isAccountsLoading } =
    useGetAccountsQuery(true);

  useEffect(() => {
    if (allAccounts) {
      dispatch(loadAllAccounts(allAccounts));

      const { accessAccounts, accountMapper } = allAccounts.reduce(
        (acc, account) => {
          if (account?.name) {
            acc.accountMapper[account.name] = account;
          }

          if (currentUser?.accounts?.includes(account?.name)) {
            acc.accessAccounts.push(account);
          }

          return acc;
        },
        { accessAccounts: [], accountMapper: {} }
      );

      dispatch(loadAccounts(accessAccounts));
      dispatch(loadAccountsMapper(accountMapper));

      dispatch(loadAccounts(accessAccounts));
    }

    return () => {
      dispatch(loadAllAccounts([]));
      dispatch(loadAccountsMapper({}));
      dispatch(loadAccounts([]));
    };
  }, [allAccounts, currentUser?.accounts, dispatch]);

  const isRPMSaaSUser = useCheckRPMSaaSAccountUser();

  useEffect(() => {
    if (isRPMView || isRPMSaaSUser) {
      setIsSidebarOpen(false);
    } else if (isOldRpmView) {
      setIsSidebarOpen(true);
    }
  }, [isRPMView, isOldRpmView, isRPMSaaSUser]);

  if (
    isLoading ||
    isAccountsLoading ||
    !Object.keys(currentUser || {}).length ||
    !accounts?.length ||
    !allAccounts?.length
  ) {
    return <Loading height="100%" width="100%" />;
  }

  return (
    <div>
      <div className={classes.root}>
        {!isReportDetailModal && (
          <>
            <Header open={false} view={view} role={role} />

            {(isRPMSaaSUser || (isRPMView && !isRPMViewLoading)) && (
              <Tooltip
                title={isSidebarOpen ? "Close Side Bar" : "Open Side Bar"}
              >
                <Fab
                  variant="circular"
                  aria-label="open drawer"
                  edge="start"
                  size="small"
                  onClick={handleToggleSidebar}
                  style={{
                    position: "absolute",
                    top: "60px",
                    left: isSidebarOpen ? "40px" : "0",
                    minHeight: "28px",
                    height: "28px",
                    width: "28px",
                    border: "1px solid #ccc",
                    boxShadow: "none",
                    zIndex: theme.zIndex.drawer + 1,
                  }}
                >
                  {isSidebarOpen ? (
                    <KeyboardDoubleArrowLeftRounded />
                  ) : (
                    <KeyboardDoubleArrowRightRounded />
                  )}
                </Fab>
              </Tooltip>
            )}

            {isSidebarOpen && (
              <Sidenav
                open={false}
                role={role}
                physician={physician}
                clinicName={clinicName}
              />
            )}
          </>
        )}

        <main
          className={classes.content}
          style={{
            backgroundColor: theme.palette.background.default,
            paddingTop: isRPMSaaSUser || isRPMView ? "85px" : "100px",
            paddingLeft:
              isRPMSaaSUser || isRPMView
                ? isSidebarOpen
                  ? "66px"
                  : "10px"
                : "80px",
          }}
        >
          <div className={classes.toolbar} />
          <Routes>
            <Route
              exact
              path="/patients"
              element={
                <ProtectedRoute
                  component={Patients}
                  role={role}
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC_REVIEWER,
                  ]}
                />
              }
            />

            {/* Old RPM View */}
            <Route
              path="/patient/rpm"
              element={
                <ProtectedRoute
                  component={PatientRPM}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  setActiveView={setActiveView}
                  authorizedRoles={[
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.USER,
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_REVIEWER,
                  ]}
                />
              }
            />

            {/* New RPM View using Clinii Embedding*/}
            <Route
              path="/rpm"
              element={
                <ProtectedRoute
                  component={RpmView}
                  authorizedRoles={[
                    USER_ROLES.ADMIN,
                    USER_ROLES.USER,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_REVIEWER,
                    USER_ROLES.RPM_USER,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/no-show-patients"
              element={
                <ProtectedRoute
                  component={NonCompliantPatients}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}
                />
              }
            />

            <Route
              exact
              path="/no-show-patients/non-enrolled-patients"
              element={
                <ProtectedRoute
                  component={NonCompliantPatients}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}
                />
              }
            />

            <Route
              exact
              path="/no-show-patients/live-call-tasks"
              element={
                <ProtectedRoute
                  component={NonCompliantPatients}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}
                />
              }
            />

            <Route
              exact
              path="/dashboard"
              element={
                <ProtectedRoute
                  component={Dashboard}
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC_REVIEWER,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/upload"
              element={
                <ProtectedRoute
                  component={ManualUpload}
                  authorizedRoles={[
                    ...new Set([
                      USER_ROLES.ADMIN,
                      USER_ROLES.USER,
                      USER_ROLES.CLINIC_ADMIN,
                      ...(CLINIC_ROLES.includes(currentUser?.role)
                        ? getRolePreferenceByAccount(
                            accounts,
                            clinicName,
                            "manual_upload",
                            "report_upload"
                          )
                        : []),
                    ]),
                  ]}
                />
              }
            />
            <Route
              exact
              path="/prohibited"
              element={
                <Prohibited
                  role={role}
                  title="Not Authorized"
                  content="You are prohibited from accessing this page."
                />
              }
            />
            <Route
              exact
              path="/tasks"
              element={
                <ProtectedRoute
                  component={Tasks}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="active"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/tasks/archived"
              element={
                <ProtectedRoute
                  component={Tasks}
                  archived={true}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="archived"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/tasks/automated"
              element={
                <ProtectedRoute
                  component={Tasks}
                  automated={true}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="automated"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/tasks/audited"
              element={
                <ProtectedRoute
                  component={Tasks}
                  audited={true}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="audited"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/tasks/audited-archived"
              element={
                <ProtectedRoute
                  component={Tasks}
                  audited={true}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="audited-archived"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/tasks/pending-approvals"
              element={
                <ProtectedRoute
                  component={Tasks}
                  audited={true}
                  role={role}
                  history={props.history}
                  navigate={navigate}
                  selectedTab="pending-approvals"
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              path="/patient/report/:reportId"
              element={<ProtectedRoute component={TransmissionReport} />}
            />

            <Route
              path="/patient/:patientId"
              element={<ProtectedRoute component={PatientProfile} />}
            />

            <Route
              path="/transmissions"
              element={
                <ProtectedRoute
                  role={role}
                  theme={theme}
                  component={Transmission}
                  authorizedRoles={[
                    USER_ROLES.ADMIN,
                    USER_ROLES.USER,
                    USER_ROLES.ASSISTANT,
                  ]}
                />
              }
            />

            <Route
              path="/transmissions/processed"
              element={
                <ProtectedRoute
                  component={Transmission}
                  theme={theme}
                  role={role}
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                  ]}
                />
              }
            />

            <Route
              path="/transmissions/clinic"
              element={
                <ProtectedRoute
                  component={Transmission}
                  theme={theme}
                  role={role}
                  authorizedRoles={[
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_REVIEWER,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />
            <Route
              path="/transmissions/clinic-review"
              element={
                <ProtectedRoute
                  component={Transmission}
                  theme={theme}
                  role={role}
                  authorizedRoles={[
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />

            <Route
              path="/transmissions/clinic-processed"
              element={
                <ProtectedRoute
                  component={Transmission}
                  theme={theme}
                  role={role}
                  authorizedRoles={[
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            />

            <Route
              exact
              path="/billing"
              element={
                <ProtectedRoute
                  component={Billing}
                  history={props.history}
                  navigate={navigate}
                  role={role}
                  setActiveView={setActiveView}
                  authorizedRoles={[USER_ROLES.ADMIN]}
                />
              }
            >
              <Route exact path="/billing/:view" element={<Billing />} />
            </Route>
            <Route
              exact
              path="/settings"
              element={
                <ProtectedRoute
                  component={Settings}
                  history={props.history}
                  navigate={navigate}
                  role={role}
                  setActiveView={setActiveView}
                  authorizedRoles={[
                    ...new Set([
                      USER_ROLES.ADMIN,
                      USER_ROLES.CLINIC_ADMIN,
                      USER_ROLES.RPM_USER,
                      ...(hasSettingsAccess
                        ? [USER_ROLES.CLINIC, USER_ROLES.PHYSICIAN]
                        : []),
                    ]),
                  ]}
                />
              }
            >
              <Route
                exact
                path="/settings/:view"
                element={
                  <Settings
                    history={props.history}
                    navigate={navigate}
                    role={role}
                    setActiveView={setActiveView}
                  />
                }
              />
            </Route>
            <Route
              exact
              path="/reports-assignment"
              element={
                <ProtectedRoute
                  component={ReportsAssigment}
                  authorizedRoles={[
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_REVIEWER,
                  ]}
                />
              }
            />
            <Route
              exact
              path="/ehr-jobs/hl7jobs"
              element={
                <ProtectedRoute
                  component={EHRJobs}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN]}
                />
              }
            />
            <Route
              exact
              path="/ehr-jobs/apijobs"
              element={
                <ProtectedRoute
                  component={EHRJobs}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN]}
                />
              }
            />
            <Route
              path="/report-templates"
              element={
                <ProtectedRoute
                  component={ReportTemplates}
                  authorizedRoles={[
                    USER_ROLES.USER,
                    USER_ROLES.ADMIN,
                    USER_ROLES.ASSISTANT,
                    USER_ROLES.CLINIC_ADMIN,
                  ]}
                />
              }
            >
              <Route index element={<Navigate to="templates" replace />} />
              <Route path=":view" element={<ReportTemplates />} />
            </Route>
            <Route
              exact
              path="/archived-reports"
              element={
                <ProtectedRoute
                  component={ArchivedReports}
                  authorizedRoles={[
                    USER_ROLES.ADMIN,
                    USER_ROLES.PHYSICIAN,
                    USER_ROLES.CLINIC_ADMIN,
                    USER_ROLES.CLINIC,
                  ]}
                />
              }
            />

            <Route
              exact
              path="/billing-amount"
              element={
                <ProtectedRoute
                  component={BillingAmount}
                  authorizedRoles={[USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN]}
                />
              }
            />

            <Route
              exact
              path="/account-configurations"
              element={
                <ProtectedRoute
                  component={AccountConfiguration}
                  navigate={navigate}
                  role={role}
                  setActiveView={setActiveView}
                  authorizedRoles={[USER_ROLES.ADMIN]}
                />
              }
            >
              <Route
                exact
                path="/account-configurations/:view"
                element={
                  <AccountConfiguration
                    navigate={navigate}
                    role={role}
                    setActiveView={setActiveView}
                  />
                }
              />
            </Route>

            <Route
              exact
              path="/wearables"
              element={
                <ProtectedRoute
                  component={WearableView}
                  authorizedRoles={[USER_ROLES.PHYSICIAN]}
                />
              }
            />

            {/* Ambulatory ECG patch worklist. Clinic-side only — Evoke staff never
                handle these reports, so the role list matches the nav gate exactly. */}
            {[
              PATCH_ROUTE,
              ...PATCH_TABS.map((tab) => patchTabRoute(tab.value)),
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute
                    component={PatchMonitoring}
                    authorizedRoles={[
                      USER_ROLES.CLINIC,
                      USER_ROLES.CLINIC_ADMIN,
                      USER_ROLES.PHYSICIAN,
                    ]}
                  />
                }
              />
            ))}

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </main>
      </div>

      <DeactivatePatientModal />
      <SessionExpiryModal
        open={showExpiryWarning}
        onLogout={handleSessionLogout}
        onExtend={extendSession}
        isExtending={isExtending}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
};

export default AppPage;
